<template>
    <loading-placeholder v-if="loading" />
    <div v-else>
        <div class="row form-group" v-for="(field,name) in widget_settings" v-if="field.type !== 'hidden'">
            <div class="col-md-12">
                <div :id="'widget-input-' + name"
                     :class="[field.search_script !== undefined && temp_active_field===name ? 'open' : '', field.search_script !== undefined ? 'dropdown' : '']">
                    <template v-if="field.type==='text'">
                        <v-text-field v-model="inner_widget[name]"
                                      @focus="innerFieldFocus(name)"
                                      @blur="innerFieldBlur(name);innerFieldFetch(name)"
                                      @keyup.enter="innerFieldEnter(name);innerFieldFetch(name)"
                                      :label="field.fieldname"
                                      :disabled="element_readonly(name)"
                                      :outlined="outlined" :dense="dense" hide-details="auto"
                                      :placeholder="field.default"
                                      :hint="field['desc']">
                        </v-text-field>
                    </template>
                    <template v-if="field.type==='numeric'">
                        <number-field v-model="inner_widget[name]"
                                      @focus="innerFieldFocus(name)"
                                      @blur="innerFieldBlur(name);innerFieldFetch(name)"
                                      @keyup.enter="innerFieldEnter(name);innerFieldFetch(name)"
                                      :label="field.fieldname"
                                      :disabled="element_readonly(name)"
                                      :outlined="outlined" :dense="dense" hide-details="auto"
                                      :placeholder="field.default"
                                      :hint="field['desc']"
                                      :min="field.min"
                                      :max="field.max"
                                      :step="Math.pow(10, -(field.decimals ? field.decimals : 0))">
                        </number-field>
                    </template>
                    <template v-else-if="field.type==='select'">
                        <v-autocomplete :value="getSettings(name, field.type)"
                                  @change="updateSettings(name, field.type, $event)"
                                  :label="field.fieldname"
                                  :disabled="element_readonly(name)"
                                  :placeholder="field.options[field.default]"
                                  :items="selectItems(field['options'])"
                                  :hint="field['desc']"
                                  :attach="'#widget-input-' + name + ' .v-select'"
                                  :outlined="outlined" :dense="dense" hide-details="auto">
                        </v-autocomplete>
                    </template>
                    <template v-else-if="field.type==='color'">
                        <colorpicker
                            :id="name"
                            v-model="inner_properties[name]"
                            @input="livePreviewChange(name, $event)"
                            :label="field.fieldname"
                            :hint="field['desc']"
                            :disabled="element_readonly(name)">
                        </colorpicker>
                    </template>
                    <template v-else-if="field.type==='font'">
                        <v-autocomplete v-model="inner_properties[name]"
                                  @change="livePreviewChange(name, inner_properties[name])"
                                  :label="field.fieldname"
                                  :disabled="element_readonly(name)"
                                  :placeholder="field.default"
                                  :items="fontsConfig"
                                  :hint="field['desc']"
                                  :attach="'#widget-input-' + name + ' .v-select'"
                                  :outlined="outlined" :dense="dense" hide-details="auto">
                            <template v-slot:item="{active, item}">
                                <span :style="{fontFamily: '\'' + item + '\''}">{{ item }}</span>
                            </template>
                        </v-autocomplete>
                    </template>
                    <template v-else-if="field.type==='date'">
                        <datepicker
                            :id="name"
                            :label="field.fieldname"
                            :placeholder="formatDate(field.default)"
                            :value="dateISOToDateUS(inner_properties[name])"
                            @input="inner_properties[name] = dateUSToDateISO($event); livePreviewChange(name, inner_properties[name])"
                            :hint="field['desc']"
                            :disabled="element_readonly(name)" />
                    </template>
                    <template v-else-if="field.type==='time'">
                        <timepicker
                            :id="name"
                            :label="field.fieldname"
                            v-model="inner_properties[name]"
                            @input="livePreviewChange(name, inner_properties[name])"
                            :hint="field['desc']"
                            :disabled="element_readonly(name)">
                        </timepicker>
                    </template>
                    <template v-else-if="field.type==='datetime'">
                        <date-time-widget-setting v-model="inner_properties[name]"
                                                  :default="field.default"
                                                  :disabled="element_readonly(name)"
                                                  :hint="field.desc"
                                                  :label="field.fieldname"
                                                  @input="livePreviewChange(name, inner_properties[name])" />
                    </template>
                    <template v-else-if="field.type==='checkbox'">
                        <Checkbox
                            class="mr-2"
                            :binary="true"
                            :modelValue="inner_properties[name]"
                            :label="field.fieldname"
                            :disabled="element_readonly(name)"
                            :hint="field['desc']"
                            true-value="1"
                            false-value="0"
                            @click="() => {
                                inner_properties[name] = inner_properties[name] === '0' ? '1' : '0';
                                livePreviewChange(name, inner_properties[name]);
                            }"
                        />
                        {{ field.fieldname }}
                    </template>

                    <template v-if="field.search_script !== undefined">
                        <ul class="dropdown-menu dropdown-search">
                            <li v-for="word in inner_search_results[name]" @mousedown.prevent="setInnerField(name, field.type, word)">
                                {{ word }}
                            </li>
                        </ul>
                        <iframe sandbox="allow-scripts" class="hidden" :id="name + '_iframe'"
                                :srcdoc="'<html><script>' + field.search_script + '</script></html>'">
                        </iframe>
                    </template>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import commonDefinitionMixin from "./commonDefinitionMixin";
import datepicker from '../../common/datepicker.vue';
import {elementsSettingsDefaults} from "./settingsDefinition";
import moment from "moment";
import { dateISOToDateUS, dateUSToDateISO } from '../../../../utils/date';
import DateTimeWidgetSetting from './components/dateTimeWidgetSetting.vue';
import LoadingPlaceholder from '../common/loadingPlaceholder.vue';
import { widgetConvertSmartValue } from '../../../../utils/widget';
import Checkbox
    from "../common/PrimeVue/Checkbox.vue";

export default {
    components: {
        Checkbox,
        LoadingPlaceholder,
        DateTimeWidgetSetting,
        datepicker,
    },
    mixins: [
        commonDefinitionMixin,
    ],
    props: [
        'value',
        'element_nr',
        'definition',
        'widget_settings',
        'widget_properties',
        'loading',
        'readonly',
        'advanced-privilege',
        'basic-privilege',
    ],
    data: function () {
        return {
            t_yes: Lang.langGet("Yes"),
            t_no: Lang.langGet("No"),

            messageListeners: {},
            inner_properties: {},
            inner_widget: {},
            inner_search_results: {},
            fontsConfig: [],
            prev_widget_id: null,

            reloadListeners: true,
        }
    },
    watch: {
        value: function () {
            this.reloadListeners = true;
            this.updateInnerProperties();
            this.prev_widget_id = this.value.id;
        },
        widget_settings: function () {
            this.reloadListeners = true;
            this.updateInnerProperties();
        },
        inner_widget: {
            handler: function () {
                if(this.temp_active_field) {
                    this.innerFieldSetter(this.temp_active_field, this.inner_widget[this.temp_active_field]);
                }
            },
            deep: true
        },
        temp_active_value: function (val, old_val) {
            if (this.inner_search_results[this.temp_active_field] !== undefined) {
                let field = this.widget_settings[this.temp_active_field];
                this.message_search(this.temp_active_field, field)
            }
        },
        loading: {
            handler(val) {
                if (val) {
                    this.destroyMessageListeners();
                } else {
                    this.initializeMessageListeners();
                }
            },
            immediate: true
        }
    },
    methods: {
        dateUSToDateISO,
        dateISOToDateUS,
        message_search: function (name, field) {
            if (field.search_script !== undefined) {
                if (document.getElementById(name + '_iframe')) {
                    let win = document.getElementById(name + '_iframe').contentWindow;
                    let pmName = name.replace(/^(WIDGET_)/,"");
                    win.postMessage('easescreen.search:' + this.inner_widget[name], "*");
                }
            }
        },
        updateInnerProperties: function () {
            this.inner_properties = this.value;
            let inner_widget = {};
            _.each(this.widget_settings, (field, name) => {
                let field_value = this.getFieldValue(field, name);

                if (this.inner_properties[name] !== field_value) {
                    this.updateSettings(name, field.type, field_value);
                }

                if(['text', 'numeric', 'color'].includes(field.type)) {
                    inner_widget[name] = field_value;
                }
            });
            this.inner_widget = inner_widget;
        },
        selectItems: function (options) {
            let items = [];
            for (let [value, name] of Object.entries(options)) {
                items.push({
                    text: name,
                    value: value
                });
            }
            return items;
        },
        getFieldValue: function (field, name) {
            let field_value = null;
            if (this.value[name] === undefined) {
                if (field.default === undefined) {
                    field_value = elementsSettingsDefaults["defaultByType"][field.type];
                } else {
                    field_value = field.default;
                }
            } else {
                field_value = this.value[name];
            }
            return field_value;
        },
        destroyMessageListeners: function () {
            _.each(this.messageListeners, (listener) => {
                window.removeEventListener("message", listener);
            });
            this.messageListeners = {};
        },
        initializeMessageListeners: function () {
            _.each(this.widget_settings, (field, name) => {
                if (field.search_script !== undefined) {
                    if (this.messageListeners['search'] === undefined) {
                        this.messageListeners['search'] = (() => {
                            if (typeof event.data === 'string' && event.data.startsWith('easescreen.search:')) {
                                let search_data = event.data.slice(18).split('=', 2);
                                const name = 'WIDGET_' + search_data[0];
                                if (this.inner_search_results.hasOwnProperty(name)) {
                                    let search_result = JSON.parse(search_data[1]);
                                    if (Array.isArray(search_result)) {
                                        Vue.set(this.inner_search_results, name, search_result.slice(0, 50));
                                    }
                                }
                            }
                        });
                        window.addEventListener("message", this.messageListeners['search']);
                    }
                    Vue.set(this.inner_search_results, name, []);
                    this.message_search(name, field);
                }
            });

            this.messageListeners['parameter'] = (() => {
                if (typeof event.data === 'string' && event.data.startsWith('easescreen.parameter:')) {
                    let parameter_data = event.data.slice(21).split('=', 2);
                    let exportfield = parameter_data[0];
                    let index = Object.values(this.widget_settings).findIndex((setting) => {
                        return setting.exportfield === exportfield;
                    })
                    if (index >= 0) {
                        let name = Object.keys(this.widget_settings)[index];
                        let type = this.widget_settings[name].type;
                        this.updateSettings(name, type, parameter_data[1]);
                    }
                }
            });
            window.addEventListener("message", this.messageListeners['parameter']);
        },
        elementProperties: function () {
            return this.definition;
        },
        initToBeScrolled: function (type, id, focus=false) {
            if (focus) {
                this.$refs[id][0].focus();
            }
            this.$emit('initToBeScrolled', {'type': type, 'id': id});
        },
        leaveElementToBeScrolled: function () {
            this.$emit('leaveElementToBeScrolled');
        },
        formatDate: function (val) {
            let date = this.datestringToDate(val);
            return date ? date.format(this.date_format_mapping[Laravel.activeDateformat]) : '';
        },
        getDateFromDatetimeFormated: function (datetimestring) {
            let date = this.datetimestringToDate(datetimestring);
            return date ? date.format(this.date_format_mapping[Laravel.activeDateformat]) : '';
        },
        getDateFromDatetime: function (datetimestring) {
            let date = this.datetimestringToDate(datetimestring);
            return date ? date.format("yyyy-MM-DD") : '';
        },
        getTimeFromDatetime: function (datetimestring) {
            let date = this.datetimestringToDate(datetimestring);
            return date ? date.format('HH:mm:ss') : '';
        },
        getDateFromTimestring: function (timestring) {
            let date = timestring === 'now' ? moment() : moment('1900-01-01T' + (timestring === null || timestring === '' ? '' : timestring));
            return date.isValid() ? date : null;
        },
        getTimeFromString: function (val) {
            let date = this.getDateFromTimestring(val);
            return date ? date.format('HH:mm:ss') : '';
        },
        storeFormatDate: function (val) {
            let date = null;
            if (val === 'now') {
                date = moment();
            } else if (val.includes('-')) {
                date = moment(val, "yyyy-MM-DD");
            } else {
                date = moment(val, this.date_format_mapping[Laravel.activeDateformat]);
            }
            return date.isValid() ? date.format("yyyy-MM-DD") : '';
        },
        datestringToDate: function (val) {
            let date = val === 'now' ? moment() : moment(val, "yyyy-MM-DD");
            return date.isValid() ? date : null;
        },
        datetimestringToDate: function (val) {
            let date = val === 'now' ? moment() : moment(val, "yyyy-MM-DDTHH:mm:ss");
            return date.isValid() ? date : null;
        },
        getLimit: function (field) {
            if (field.type === 'numeric') {
                return {
                    min: Number(field.min),
                    max: Number(field.max),
                    empty: 1
                }
            } else if (field.type === 'color') {
                return {
                    min: 0,
                    max: 0xFFFFFFFF,
                    color: 1,
                    empty: 1
                }
            } else {
                return {};
            }
        },
        innerFieldFetch: function (name) {
            this.inner_widget[name] = this.inner_properties[name];
            this.livePreviewChange(name, this.inner_properties[name]);
        },
        setInnerField: function (name, type, val) {
            this.inner_widget[name] = val;
        },
        getSettings: function (name, type) {
            let field_value = null;
            if (type === 'date') {
                field_value = this.formatDate(this.inner_properties[name]);
            } else if (type === 'time') {
                field_value = this.getTimeFromString(this.inner_properties[name]);
            } else if (type === 'datetime') {
                if (name.endsWith('_DATE')) {
                    name = name.substr(0, name.length-5);
                    field_value = this.getDateFromDatetimeFormated(this.inner_properties[name]);
                } else if (name.endsWith('_TIME')) {
                    name = name.substr(0, name.length-5);
                    field_value = this.getTimeFromDatetime(this.inner_properties[name]);
                }
            } else if (type === 'checkbox') {
                field_value = this.inner_properties[name] == '1' ? '1' : '0';
            } else {
                field_value = this.inner_properties[name] ?? '';
            }
            return widgetConvertSmartValue(field_value);
        },
         updateSettings: function (name, type, val) {
            let field_value = null;
            let field_name = name;

            if (type === 'datetime') {
                if (name.endsWith('_DATE')) {
                    field_name = name.substr(0, name.length-5);
                    field_value = this.storeFormatDate(val) + 'T' + this.getTimeFromDatetime(this.inner_properties[field_name]);
                } else if (name.endsWith('_TIME')) {
                    field_name = name.substr(0, name.length-5);
                    field_value = this.getDateFromDatetime(this.inner_properties[field_name]) + 'T' + this.getTimeFromString(val);
                } else {
                    field_value = val;
                }
                if (field_value === 'T') {
                    field_value = '';
                }
            } else if (type === 'date') {
                field_value = this.storeFormatDate(val);
            } else if (type === 'time') {
                field_value = this.getTimeFromString(val);
            } else if (type === 'checkbox') {
                field_value = (val === '0' || val === false) ? '0' : '1';
            } else {
                field_value = val;
            }

            if (this.inner_properties[field_name] !== field_value) {
                Vue.set(this.inner_properties, field_name, field_value);
                if (['text', 'numeric'].includes(type)) {
                    Vue.set(this.inner_widget, name, field_value);
                } else if (type === 'color') {
                    if (this.inner_properties[field_name].toLowerCase() === field_value.toLowerCase()) {
                        return;
                    }
                    Vue.set(this.inner_widget, name, field_value);
                }
                this.livePreviewChange(field_name, field_value);
            }
        },
        livePreviewChange: function(name, val) {
            console.log(name, val);
            if (this.widget_properties['widget-live-preview'] === '1' && document.getElementById('widget-live-preview-' + this.element_nr)) {
                document.getElementById('widget-live-preview-' + this.element_nr).contentWindow.postMessage(
                    "easescreen.parameter:" + this.widget_settings[name].exportfield + '=' + widgetConvertSmartValue(val), '*');
            }
            EaseEventBus.fire('setWidgetQuery');
        }
    },
    created: function () {
        this.updateInnerProperties();
    },
    mounted: function () {
        if (this.esignConnectionDirect === '1') {
            this.esign_manually = false;
        }
        this.esign_check = true;
        axios.get('/esign/' + Laravel.activeProject).then((response) => {
            if (response.data.doorsigns.length === 0 && response.data.boards.length === 0) {
                this.esign_manually = true;
            } else {
                this.esign = _.assign({}, this.esign, response.data);
            }

            this.esign_check = false;
        }).catch((error) => {
            if (error.response.status === 401) {
                window.location = '/login';
            } else {
                this.esign_manually = true;
                this.esign_check = false;
            }
        });

        axios.get('/content/fonts/load/' + Laravel.activeProject).then((response) => {
            this.fontsConfig = Object.keys(response.data);
        }).catch((error) => {
            this.fontsConfig = [];
        });
    },
    updated: function () {
        if (this.reloadListeners) {
            this.$nextTick(() => {
                this.$nextTick(() => {
                    this.destroyMessageListeners();
                    this.initializeMessageListeners();
                });
            });
            this.reloadListeners = false;
        }
    },
    beforeDestroy: function () {
        this.destroyMessageListeners();
    }
}
</script>
